/// Time + arc helpers for astronavigation. Pure functions, no DOM.
///
/// Two natural axes:
///   - time vs arc: the Earth turns 360° in 24h, so 1h = 15° and
///     1m of time = 15' of arc. All angles in this package are
///     degrees decimal unless explicitly minutes/seconds.
///   - the Julian Date (JD) is the canonical "days since
///     -4712 Jan 1 noon UT", which lets every astronomical
///     formula take a single scalar instead of carrying a Date.

/// Julian Date for a UTC `Date`. Accurate to a second for any
/// reasonable date — well below the precision navigators care about.
export function julianDate(date: Date): number {
  // Algorithm: convert to fractional day-since-J2000 then offset.
  // Using the Gregorian-calendar-aware formula from Meeus, eq. 7.1.
  let Y = date.getUTCFullYear();
  let M = date.getUTCMonth() + 1; // JS months are 0-indexed
  const D =
    date.getUTCDate() +
    (date.getUTCHours() +
      date.getUTCMinutes() / 60 +
      date.getUTCSeconds() / 3600 +
      date.getUTCMilliseconds() / 3_600_000) /
      24;
  if (M <= 2) {
    Y -= 1;
    M += 12;
  }
  const A = Math.floor(Y / 100);
  const B = 2 - A + Math.floor(A / 4);
  return (
    Math.floor(365.25 * (Y + 4716)) +
    Math.floor(30.6001 * (M + 1)) +
    D +
    B -
    1524.5
  );
}

/// Days since the J2000.0 epoch (2000-01-01 12:00 UTC). The "n" used
/// throughout low-precision ephemeris formulae.
export function daysSinceJ2000(date: Date): number {
  return julianDate(date) - 2451545.0;
}

/// Normalise a longitude / hour-angle to the half-open [0, 360°) range.
export function normalize360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/// Normalise to (−180°, 180°] — useful for east/west signed bearings.
export function normalizeSigned180(deg: number): number {
  const d = normalize360(deg);
  return d > 180 ? d - 360 : d;
}

/// Degrees → radians (the package's working unit is degrees, but the
/// trig calls need radians).
export const DEG = Math.PI / 180;

/// Radians → degrees.
export const RAD = 180 / Math.PI;

/// 1h of time = 15° of arc. Useful for displaying RA / GHA / LHA in
/// either form depending on what the student is being asked.
export function hoursToDegrees(hours: number): number {
  return hours * 15;
}

export function degreesToHours(deg: number): number {
  return deg / 15;
}

/// Render a degrees value as a sailor's "DD° MM.M'" (the form printed
/// on every Almanac page). Negative values get an explicit minus sign;
/// the caller is responsible for choosing the right hemisphere letter
/// where context matters.
export function formatDegMin(deg: number, precision = 1): string {
  const sign = deg < 0 ? "−" : "";
  const a = Math.abs(deg);
  const d = Math.floor(a);
  const m = (a - d) * 60;
  return `${sign}${d}° ${m.toFixed(precision)}′`;
}

/// Render a declination as "12° 34.5' N" / "5° 12.3' S".
export function formatDeclination(deg: number, precision = 1): string {
  const hemi = deg >= 0 ? "N" : "S";
  return `${formatDegMin(Math.abs(deg), precision)} ${hemi}`;
}
